import React, { forwardRef } from "react";
import Button from "./Button";
import { cn } from "../../../lib/utils";

/**
 * ToggleButton Molecule - Button with active/inactive states
 * Used for tabs, view toggles, and state switches
 *
 * @component
 * @example
 * <ToggleButton active={activeTab === 'events'} onClick={() => setActiveTab('events')}>
 *   Events
 * </ToggleButton>
 */
const ToggleButton = forwardRef(
  (
    {
      children,
      active = false,
      variant = "default",
      size = "md",
      rounded = "3xl",
      className = "",
      disabled = false,
      leftIcon = null,
      rightIcon = null,
      ...props
    },
    ref
  ) => {
    const getStyles = () => {
      switch (variant) {
        case "default":
          return active
            ? "bg-red-400 text-white border-red-400 hover:bg-red-500 hover:border-red-500 "
            : "bg-transparent text-gray-700 border-gray-300 hover:bg-gray-50 ";
        case "solid":
          return active
            ? "bg-red-500 text-white hover:bg-red-600 "
            : "bg-gray-200 text-gray-700 hover:bg-gray-300 ";
        case "ghost":
          return active
            ? "bg-red-50 text-red-500 hover:bg-red-100 "
            : "bg-transparent text-gray-600 hover:bg-gray-100 ";
        default:
          return "";
      }
    };

    return (
      <Button
        ref={ref}
        variant="ghost" // Base variant
        size={size}
        rounded={rounded}
        className={cn(
          "border-2 border-transparent", // Base border for alignment
          getStyles(),
          className
        )}
        disabled={disabled}
        leftIcon={leftIcon}
        rightIcon={rightIcon}
        aria-pressed={active}
        {...props}
      >
        {children}
      </Button>
    );
  }
);

ToggleButton.displayName = "ToggleButton";

export default ToggleButton;
